/**
 * Economy store for managing game currency and resources.
 */

import { create } from 'zustand';
import { useDevSettingsStore } from './devSettingsStore';

interface EconomyState {
  currency: number;
  lastIncome: number | null; // Last income amount received (for display)
  lastIncomeTimestamp: number | null; // When the last income was received
  addCurrency: (amount: number) => void;
  addIncome: (amount: number) => void; // Specifically for periodic income (shows indicator)
  deductCurrency: (amount: number) => boolean;
  hasEnoughCurrency: (amount: number) => boolean;
  resetCurrency: () => void;
}

function getStartingCurrency(): number {
  try {
    const state = useDevSettingsStore.getState();
    const value = state.settings.buildings.startingCurrency;
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
  } catch {
    // Dev settings may not be initialized yet; fall back to default
  }
  return 1000;
}

export const useEconomyStore = create<EconomyState>((set, get) => ({
  // Neutral initial value; we explicitly initialize from dev settings when the game starts.
  currency: 1001,
  lastIncome: null,
  lastIncomeTimestamp: null,

  addCurrency: (amount: number) => {
    set((state) => ({
      currency: Math.max(0, state.currency + amount),
    }));
  },

  addIncome: (amount: number) => {
    set((state) => ({
      currency: Math.max(0, state.currency + amount),
      lastIncome: amount,
      lastIncomeTimestamp: Date.now(),
    }));
  },

  deductCurrency: (amount: number) => {
    const currentCurrency = get().currency;
    if (currentCurrency >= amount) {
      set({ currency: currentCurrency - amount });
      return true;
    }
    return false;
  },

  hasEnoughCurrency: (amount: number) => {
    return get().currency >= amount;
  },

  resetCurrency: () => {
    set({ currency: getStartingCurrency() });
  },
}));

/**
 * Initialize the economy store from the current dev settings.
 * Call this when a new game starts so devs can tweak startingCurrency beforehand.
 */
export function initializeEconomyFromDevSettings() {
  useEconomyStore.setState({ currency: getStartingCurrency() });
}
